/**
 * F-099 / F-050 / F-120 — the parts list of a D&D line (`line_parts` on the API; one part = one PIQ
 * part): description, sourcing preset, substrate (Manufactured – In House only), W×H, pieces,
 * sides, part notes, modifiers (PIQ operation codes) and the supplier info the sourcing opens.
 *
 * Mirrors `App\Support\DdLineParts` (the API's single validator): the editor works on string
 * drafts, `draftsToParts()` turns them into the wire shape and lists what the server would refuse,
 * `partsSupplierGaps()` / `partsBuildResolved()` are the two gates (Complete Triage, Product
 * Review) so the cue appears before an optimistic save rolls back.
 */
import type { OriginSpec } from '@/types/origin'
import {
  PART_SOURCING,
  findPartOperation,
  findPartSourcing,
  findSupplierLayout,
  type PartOperation,
  type PartSourcingKey,
  type SupplierLayoutKey
} from '@/lib/dd-queue/data/partOperations'
import { supplierLayoutForSourcing, supplierMissingFields } from '@/lib/dd-queue/partSupplier'

export const LINE_PARTS_MAX = 20
export const LINE_PART_MODIFIERS_MAX = 30

export type PartSides = 'single' | 'double'

export interface LinePartSubstrate {
  id: string | null
  piq: string | null
  label: string
}

export interface LinePartModifier {
  code: string
  qty?: number
  spacing_in?: number
  size?: string
  size_text?: string | null
  bends?: number
}

export interface LinePartSupplier {
  layout: SupplierLayoutKey
  fields: Record<string, string>
}

/** One stored part — the API's normalised shape. */
export interface LinePart {
  ordinal: number
  description: string
  sourcing: PartSourcingKey
  substrate: LinePartSubstrate | null
  width: number | null
  height: number | null
  pieces: number
  sides: PartSides | null
  notes: string | null
  modifiers: LinePartModifier[]
  supplier: LinePartSupplier | null
}

export type LinePartsSource = 'intake' | 'human'

/** Editor row for one modifier — inputs are strings until they are sent. */
export interface LinePartModifierDraft {
  id: string
  code: string
  qty: string
  spacing_in: string
  size: string
  size_text: string
  bends: string
}

/** Editor row for one part. */
export interface LinePartDraft {
  id: string
  description: string
  sourcing: PartSourcingKey
  substrate: LinePartSubstrate | null
  width: string
  height: string
  pieces: string
  sides: PartSides | ''
  notes: string
  modifiers: LinePartModifierDraft[]
  /** Supplier boxes by field key — kept across sourcing changes, sent only for the open layout. */
  supplierFields: Record<string, string>
}

let draftSeq = 0
function nextDraftId(prefix: string): string {
  draftSeq += 1
  return `${prefix}-${draftSeq}`
}

export const DEFAULT_PART_SOURCING: PartSourcingKey = 'manufactured_in_house'

export function sourcingLabel(key: string | null | undefined): string {
  return (key && findPartSourcing(key)?.label) || (key ?? '')
}

export function sourcingOptions(): Array<{ key: PartSourcingKey; label: string }> {
  return PART_SOURCING.map((s) => ({ key: s.key, label: s.label }))
}

/** Only Manufactured – In House parts have a stock and dimensions (Candace: vended lists none). */
export function sourcingHasBuild(key: string | null | undefined): boolean {
  return !!key && (findPartSourcing(key)?.needs_substrate ?? true)
}

export function emptyPartDraft(overrides: Partial<LinePartDraft> = {}): LinePartDraft {
  return {
    id: nextDraftId('part'),
    description: '',
    sourcing: DEFAULT_PART_SOURCING,
    substrate: null,
    width: '',
    height: '',
    pieces: '1',
    sides: '',
    notes: '',
    modifiers: [],
    supplierFields: {},
    ...overrides
  }
}

export function emptyModifierDraft(code = ''): LinePartModifierDraft {
  return {
    id: nextDraftId('mod'),
    code,
    qty: '',
    spacing_in: '',
    size: '',
    size_text: '',
    bends: ''
  }
}

function numText(value: number | null | undefined): string {
  return value == null ? '' : String(value)
}

/** Stored parts → editor rows. */
export function partsToDrafts(parts: readonly LinePart[]): LinePartDraft[] {
  return parts.map((p) =>
    emptyPartDraft({
      description: p.description ?? '',
      sourcing: p.sourcing,
      substrate: p.substrate ? { ...p.substrate } : null,
      width: numText(p.width),
      height: numText(p.height),
      pieces: numText(p.pieces) || '1',
      sides: p.sides ?? '',
      notes: p.notes ?? '',
      modifiers: (p.modifiers ?? []).map((m) => ({
        ...emptyModifierDraft(m.code),
        qty: numText(m.qty),
        spacing_in: numText(m.spacing_in),
        size: m.size ?? '',
        size_text: m.size_text ?? '',
        bends: numText(m.bends)
      })),
      supplierFields: { ...(p.supplier?.fields ?? {}) }
    })
  )
}

/** Where a draft issue sits: a part's field, one of its modifier rows (`modifier:<index>`) or a supplier box (`supplier:<key>`). */
export type PartIssueField =
  | 'description'
  | 'width'
  | 'height'
  | 'pieces'
  | 'notes'
  | 'modifiers'
  | `modifier:${number}`
  | `supplier:${string}`

/** Which control of a modifier row an issue belongs to — a HOLES row has a size pick and a quantity. */
export type ModifierIssueInput = 'operation' | 'qty' | 'spacing_in' | 'bends' | 'size'

/** One draft issue pinned to the input it belongs to (owner, staging 2026-10-07: the error must sit where the user types). */
export interface PartFieldIssue {
  /** Index into the drafts list. */
  part: number
  field: PartIssueField
  /** For a `modifier:<index>` field: the row's control the message is about. */
  input?: ModifierIssueInput
  /** Short, for the inline line under the input ("Must be a number greater than 0."). */
  message: string
}

/** A modifier row's issue without the part / operation prefix, keyed to the control it is about. */
export interface ModifierShortIssue {
  input: ModifierIssueInput
  message: string
}

export interface DraftsToPartsResult {
  parts: LinePart[]
  /** What the API would refuse — "Part 2: width must be a number greater than 0". */
  issues: string[]
  /** The same issues, one per input, for the editor's inline marks. */
  fieldIssues: PartFieldIssue[]
}

function parsePositiveNumber(raw: string): number | null | 'invalid' {
  const text = raw.trim()
  if (text === '') return null
  const n = Number(text)
  if (!Number.isFinite(n) || n <= 0) return 'invalid'
  return n
}

function parsePositiveInt(raw: string): number | null | 'invalid' {
  const text = raw.trim()
  if (text === '') return null
  const n = Number(text)
  if (!Number.isInteger(n) || n < 1) return 'invalid'
  return n
}

/**
 * One modifier row → the wire shape, with the input its operation kind needs. Shared with the
 * Modify form's optional picker (F-099 slice 7): a bad or empty row is reported in `issues` and
 * left out.
 */
export function modifierFromDraft(
  draft: Omit<LinePartModifierDraft, 'id'>,
  partLabel: string,
  index: number,
  issues: string[],
  /** The same messages without the "Part N: OPERATION — " prefix, each keyed to the row's control it is about — the editor's inline mark. */
  shortIssues?: ModifierShortIssue[]
): LinePartModifier | null {
  const code = draft.code.trim()
  if (code === '') {
    issues.push(`${partLabel}: modifier ${index} — pick an operation.`)
    shortIssues?.push({ input: 'operation', message: 'Pick an operation.' })
    return null
  }
  const found = findPartOperation(code)
  if (!found) {
    issues.push(`${partLabel}: modifier ${index} has an unknown code "${code}".`)
    shortIssues?.push({ input: 'operation', message: `Unknown code "${code}".` })
    return null
  }
  const op: PartOperation = found.operation
  const where = `${partLabel}: ${op.name}`
  const issue = (input: ModifierIssueInput, short: string) => {
    issues.push(`${where} — ${short.charAt(0).toLowerCase()}${short.slice(1)}`)
    shortIssues?.push({ input, message: short })
  }
  const out: LinePartModifier = { code }
  switch (op.input) {
    case 'qty': {
      const qty = parsePositiveInt(draft.qty)
      if (qty === null || qty === 'invalid')
        issue('qty', 'Quantity must be a whole number of at least 1.')
      else out.qty = qty
      break
    }
    case 'spacing_in': {
      const spacing = parsePositiveNumber(draft.spacing_in)
      if (spacing === null || spacing === 'invalid')
        issue('spacing_in', 'Spacing (inches) must be a number greater than 0.')
      else out.spacing_in = spacing
      break
    }
    case 'bends': {
      const bends = parsePositiveInt(draft.bends)
      if (bends === null || bends === 'invalid')
        issue('bends', 'Bends must be a whole number of at least 1.')
      else out.bends = bends
      break
    }
    case 'size_qty': {
      const sizes = (op.sizes ?? []).map((s) => s.value)
      const size = draft.size.trim()
      if (!sizes.includes(size)) issue('size', 'Pick a hole size.')
      else {
        out.size = size
        if (size === 'custom') {
          const text = draft.size_text.trim()
          out.size_text = text !== '' ? text : null
        }
      }
      const qty = parsePositiveInt(draft.qty)
      if (qty === null || qty === 'invalid')
        issue('qty', 'Quantity must be a whole number of at least 1.')
      else out.qty = qty
      break
    }
    case 'none':
    default:
      break
  }
  return out
}

/**
 * Editor rows → the wire shape (the API's normalised form: non-manufactured parts carry no
 * substrate / dimensions, the supplier block only for the layout the sourcing opens, empty boxes
 * dropped) plus the list of what the API would refuse. A part with issues still appears in
 * `parts` with the bad numbers left null, so counts and labels stay stable while the user fixes it.
 */
export function draftsToParts(drafts: readonly LinePartDraft[]): DraftsToPartsResult {
  const issues: string[] = []
  const fieldIssues: PartFieldIssue[] = []
  if (drafts.length > LINE_PARTS_MAX) issues.push(`At most ${LINE_PARTS_MAX} parts.`)

  const parts: LinePart[] = drafts.map((d, i) => {
    const ordinal = i + 1
    const description = d.description.trim()
    const label = description ? `Part ${ordinal} (${description})` : `Part ${ordinal}`
    const issue = (field: PartIssueField, long: string, short: string) => {
      issues.push(`${label}: ${long}`)
      fieldIssues.push({ part: i, field, message: short })
    }
    const sourcing: PartSourcingKey = findPartSourcing(d.sourcing)
      ? d.sourcing
      : DEFAULT_PART_SOURCING
    const hasBuild = sourcingHasBuild(sourcing)
    if (description.length > 255)
      issue('description', 'description must be at most 255 characters.', 'At most 255 characters.')

    let width: number | null = null
    let height: number | null = null
    if (hasBuild) {
      const w = parsePositiveNumber(d.width)
      const h = parsePositiveNumber(d.height)
      if (w === 'invalid')
        issue('width', 'width must be a number greater than 0.', 'Must be a number greater than 0.')
      else width = w
      if (h === 'invalid')
        issue(
          'height',
          'height must be a number greater than 0.',
          'Must be a number greater than 0.'
        )
      else height = h
    }

    let pieces = 1
    const p = parsePositiveInt(d.pieces)
    if (p === 'invalid')
      issue(
        'pieces',
        'pieces must be a whole number of at least 1.',
        'Must be a whole number of at least 1.'
      )
    else if (p !== null) pieces = p

    const notes = d.notes.trim()
    if (notes.length > 2000)
      issue('notes', 'part notes must be at most 2000 characters.', 'At most 2000 characters.')

    if (d.modifiers.length > LINE_PART_MODIFIERS_MAX)
      issue(
        'modifiers',
        `at most ${LINE_PART_MODIFIERS_MAX} modifiers.`,
        `At most ${LINE_PART_MODIFIERS_MAX} modifiers.`
      )
    const modifiers: LinePartModifier[] = []
    d.modifiers.forEach((m, j) => {
      const short: ModifierShortIssue[] = []
      const mod = modifierFromDraft(m, label, j + 1, issues, short)
      for (const { input, message } of short)
        fieldIssues.push({ part: i, field: `modifier:${j}`, input, message })
      if (mod) modifiers.push(mod)
    })

    let supplier: LinePartSupplier | null = null
    const layout = supplierLayoutForSourcing(sourcing)
    if (layout) {
      const def = findSupplierLayout(layout)
      const fields: Record<string, string> = {}
      for (const f of def?.fields ?? []) {
        const v = (d.supplierFields[f.key] ?? '').trim()
        if (v.length > 2000)
          issue(
            `supplier:${f.key}`,
            `${f.label} must be at most 2000 characters.`,
            'At most 2000 characters.'
          )
        if (v !== '') fields[f.key] = v
      }
      if (Object.keys(fields).length > 0) supplier = { layout, fields }
    }

    const substrate =
      hasBuild && d.substrate && d.substrate.label.trim() !== '' ? { ...d.substrate } : null

    return {
      ordinal,
      description,
      sourcing,
      substrate,
      width,
      height,
      pieces,
      sides: d.sides === 'single' || d.sides === 'double' ? d.sides : null,
      notes: notes !== '' ? notes : null,
      modifiers,
      supplier
    }
  })

  return { parts, issues, fieldIssues }
}

/**
 * The inline message for one input, or '' — the editor's lookup. For a modifier row, `input`
 * narrows it to one control (the size pick / the quantity of a HOLES row); without it the row's
 * first message answers, whichever control it is about.
 */
export function partFieldIssue(
  fieldIssues: readonly PartFieldIssue[],
  part: number,
  field: PartIssueField,
  input?: ModifierIssueInput
): string {
  return (
    fieldIssues.find(
      (f) => f.part === part && f.field === field && (input === undefined || f.input === input)
    )?.message ?? ''
  )
}

/** "Part 2 (Stand-off hardware)" / "Part 2". */
export function partLabel(part: Pick<LinePart, 'ordinal' | 'description'>): string {
  const description = (part.description ?? '').trim()
  return description ? `Part ${part.ordinal} (${description})` : `Part ${part.ordinal}`
}

export interface PartSupplierGap {
  ordinal: number
  description: string
  missing: string[]
}

/**
 * Complete Triage gate (Candace: a hardware or vended part whose supplier info is not filled out
 * "cannot move past Triage"; Amanda: both layouts, "N/A" counts). Mirrors the API's
 * `triage_supplier_info_missing` refusal.
 */
export function partsSupplierGaps(parts: readonly LinePart[]): PartSupplierGap[] {
  const out: PartSupplierGap[] = []
  for (const part of parts) {
    const layout = supplierLayoutForSourcing(part.sourcing)
    if (!layout) continue
    const missing = supplierMissingFields(layout, part.supplier?.fields ?? {})
    if (missing.length === 0) continue
    out.push({ ordinal: part.ordinal, description: part.description, missing })
  }
  return out
}

/** One line per part for the "before completing Triage" hint and toasts. */
export function supplierGapsText(gaps: readonly PartSupplierGap[]): string {
  return gaps.map((g) => `${partLabel(g)}: ${g.missing.join(', ')}`).join('; ')
}

/** Ordinals of the parts that need a substrate and have none (Product Review gate, F-120). */
export function partsMissingSubstrate(parts: readonly LinePart[]): number[] {
  return parts
    .filter((p) => sourcingHasBuild(p.sourcing) && !(p.substrate?.label ?? '').trim())
    .map((p) => p.ordinal)
}

/** With a parts list the parts decide: hardware / vended parts never need a stock. */
export function partsBuildResolved(parts: readonly LinePart[]): boolean {
  return partsMissingSubstrate(parts).length === 0
}

function inches(value: number | null | undefined): string | null {
  return value == null ? null : `${value}"`
}

/** `24" × 36"` or null. */
export function partDimensionsText(part: Pick<LinePart, 'width' | 'height'>): string | null {
  const w = inches(part.width)
  const h = inches(part.height)
  if (w && h) return `${w} × ${h}`
  if (w) return `W ${w}`
  if (h) return `H ${h}`
  return null
}

/** "GROM 4 CNR ×4", "GROM TOP & SPC · 12\" spacing", "HOLES .25 ×4", "HEAT BENDING · 2 bends", "APP MASK". */
export function modifierText(modifier: LinePartModifier): string {
  const found = findPartOperation(modifier.code)
  const parts: string[] = [modifier.code]
  if (modifier.size != null) {
    const sizeLabel =
      found?.operation.sizes?.find((s) => s.value === modifier.size)?.label ?? modifier.size
    parts.push(
      modifier.size === 'custom' && modifier.size_text
        ? `custom (${modifier.size_text})`
        : sizeLabel
    )
  }
  if (modifier.qty != null) parts.push(`×${modifier.qty}`)
  if (modifier.spacing_in != null) parts.push(`· ${modifier.spacing_in}" spacing`)
  if (modifier.bends != null)
    parts.push(`· ${modifier.bends} bend${modifier.bends === 1 ? '' : 's'}`)
  return parts.join(' ')
}

/**
 * One line per part for the client proof packet: build facts only — the supplier info never
 * prints there (Candace: "the information is only for our team").
 */
export function partPacketText(part: LinePart): string {
  const bits: string[] = []
  const description = (part.description ?? '').trim()
  if (description) bits.push(description)
  if (sourcingHasBuild(part.sourcing)) {
    if (part.substrate?.label) {
      bits.push(
        part.substrate.piq
          ? `${part.substrate.label} (${part.substrate.piq})`
          : part.substrate.label
      )
    }
    const dims = partDimensionsText(part)
    if (dims) bits.push(dims)
  } else {
    bits.push(sourcingLabel(part.sourcing))
  }
  if (part.pieces > 1) bits.push(`×${part.pieces}`)
  if (part.sides === 'double') bits.push('Double-sided')
  if (part.modifiers.length > 0) bits.push(part.modifiers.map(modifierText).join(', '))
  return bits.join(' · ')
}

/** Operation name for a code ("Grommets — 4 corners" style comes from the catalog's `name`). */
export function modifierName(code: string): string {
  return findPartOperation(code)?.operation.name ?? code
}

function sidesFromSpec(value: string | null | undefined): PartSides | null {
  if (!value) return null
  if (/double/i.test(value)) return 'double'
  if (/single/i.test(value)) return 'single'
  return null
}

function substrateFromSpec(
  ref: { id: string; piq?: string; label: string } | null | undefined
): LinePartSubstrate | null {
  if (!ref || !ref.label) return null
  return { id: ref.id || null, piq: ref.piq || ref.id || null, label: ref.label }
}

/** Graphic method id of the Sign Builder's applied-vinyl option (mockSignBuilderCatalog). */
const APPLIED_VINYL_METHOD_ID = 'vinyl-applied'

/**
 * The parts a Sales spec implies, offered in Triage when the line has no stored parts yet
 * (describe-mode lines carry none — D&D builds the list):
 * - Part 1 = the main build (substrate, W×H, pieces, sides);
 * - the extra stock sections of a multi-part build (DD-MULTIPART-002), in order;
 * - "Applied vinyl — <stock>" when the graphic method is applied vinyl with a vinyl stock
 *   (same size as the face it covers).
 * A part Sales marked vended (`spec.vended` / `sections[*].vended`, F-120) seeds as Vended –
 * Drop Shipped with no stock or size (Candace #5); everything else is Manufactured – In House.
 * D&D fills the supplier info in Triage.
 */
export function intakePartsFromSpec(spec: OriginSpec | null | undefined): LinePart[] {
  if (!spec || spec.build_mode === 'describe') return []
  const sections = spec.sections ?? []
  const main = sections[0]
  const out: LinePart[] = []

  const mainVended = spec.vended === true || main?.vended === true
  const mainSubstrate = mainVended
    ? null
    : substrateFromSpec(main?.substrate ?? spec.base_substrate)
  const mainWidth = mainVended ? null : (main?.width ?? spec.width ?? null)
  const mainHeight = mainVended ? null : (main?.height ?? spec.height ?? null)
  const mainPieces = main?.pieces ?? spec.pieces ?? 1
  const mainSides = mainVended ? null : sidesFromSpec(main?.single_double ?? spec.single_double)
  if (
    mainVended ||
    mainSubstrate ||
    mainWidth != null ||
    mainHeight != null ||
    sections.length > 0
  ) {
    out.push({
      ordinal: 1,
      description: main?.label?.trim() ?? '',
      sourcing: mainVended ? 'vended_drop_shipped' : DEFAULT_PART_SOURCING,
      substrate: mainSubstrate,
      width: mainWidth,
      height: mainHeight,
      pieces:
        Number.isInteger(mainPieces) && (mainPieces as number) >= 1 ? (mainPieces as number) : 1,
      sides: mainSides,
      notes: main?.notes?.trim() || null,
      modifiers: [],
      supplier: null
    })
  }

  for (const s of sections.slice(1)) {
    const vended = s.vended === true
    out.push({
      ordinal: out.length + 1,
      description: s.label?.trim() ?? '',
      sourcing: vended ? 'vended_drop_shipped' : DEFAULT_PART_SOURCING,
      substrate: vended ? null : substrateFromSpec(s.substrate),
      width: vended ? null : (s.width ?? null),
      height: vended ? null : (s.height ?? null),
      pieces: Number.isInteger(s.pieces) && s.pieces >= 1 ? s.pieces : 1,
      sides: vended ? null : sidesFromSpec(s.single_double),
      notes: s.notes?.trim() || null,
      modifiers: [],
      supplier: null
    })
  }

  // The vinyl is applied on the main face we print — a vended main build has none.
  if (
    !mainVended &&
    spec.graphic_method?.id === APPLIED_VINYL_METHOD_ID &&
    spec.vinyl_type?.label
  ) {
    out.push({
      ordinal: out.length + 1,
      description: `Applied vinyl — ${spec.vinyl_type.label}`,
      sourcing: DEFAULT_PART_SOURCING,
      substrate: substrateFromSpec(spec.vinyl_type),
      width: mainWidth,
      height: mainHeight,
      pieces: 1,
      sides: null,
      notes: null,
      modifiers: [],
      supplier: null
    })
  }

  return out
}

/** Stored list, or the intake seed while nothing was ever stored (a human clear stays empty). */
export function initialPartsForLine(args: {
  parts: readonly LinePart[] | null | undefined
  partsSource: LinePartsSource | null | undefined
  spec: OriginSpec | null | undefined
}): { parts: LinePart[]; fromIntake: boolean } {
  if (args.parts && args.parts.length > 0)
    return { parts: args.parts.map((p) => ({ ...p })), fromIntake: false }
  if (args.partsSource) return { parts: [], fromIntake: false }
  return { parts: intakePartsFromSpec(args.spec), fromIntake: true }
}

/** Snapshot value → typed list (anything malformed is dropped rather than crashing the queue). */
export function partsFromSnapshot(raw: unknown): LinePart[] {
  if (!Array.isArray(raw)) return []
  const out: LinePart[] = []
  raw.forEach((item, i) => {
    if (!item || typeof item !== 'object') return
    const p = item as Record<string, unknown>
    const sourcing =
      typeof p.sourcing === 'string' && findPartSourcing(p.sourcing)
        ? (p.sourcing as PartSourcingKey)
        : null
    if (!sourcing) return
    const substrate =
      p.substrate &&
      typeof p.substrate === 'object' &&
      typeof (p.substrate as { label?: unknown }).label === 'string'
        ? {
            id:
              typeof (p.substrate as { id?: unknown }).id === 'string'
                ? ((p.substrate as { id: string }).id as string)
                : null,
            piq:
              typeof (p.substrate as { piq?: unknown }).piq === 'string'
                ? ((p.substrate as { piq: string }).piq as string)
                : null,
            label: (p.substrate as { label: string }).label
          }
        : null
    const modifiers: LinePartModifier[] = Array.isArray(p.modifiers)
      ? (p.modifiers as unknown[]).flatMap((m) => {
          if (!m || typeof m !== 'object' || typeof (m as { code?: unknown }).code !== 'string')
            return []
          const mm = m as Record<string, unknown>
          const mod: LinePartModifier = { code: mm.code as string }
          if (typeof mm.qty === 'number') mod.qty = mm.qty
          if (typeof mm.spacing_in === 'number') mod.spacing_in = mm.spacing_in
          if (typeof mm.size === 'string') mod.size = mm.size
          if (typeof mm.size_text === 'string') mod.size_text = mm.size_text
          if (typeof mm.bends === 'number') mod.bends = mm.bends
          return [mod]
        })
      : []
    const supplierRaw =
      p.supplier && typeof p.supplier === 'object' ? (p.supplier as Record<string, unknown>) : null
    const supplier: LinePartSupplier | null =
      supplierRaw &&
      typeof supplierRaw.layout === 'string' &&
      findSupplierLayout(supplierRaw.layout)
        ? {
            layout: supplierRaw.layout as SupplierLayoutKey,
            fields: Object.fromEntries(
              Object.entries(
                supplierRaw.fields && typeof supplierRaw.fields === 'object'
                  ? (supplierRaw.fields as Record<string, unknown>)
                  : {}
              ).filter(([, v]) => typeof v === 'string') as [string, string][]
            )
          }
        : null
    out.push({
      ordinal: typeof p.ordinal === 'number' ? p.ordinal : i + 1,
      description: typeof p.description === 'string' ? p.description : '',
      sourcing,
      substrate,
      width: typeof p.width === 'number' ? p.width : null,
      height: typeof p.height === 'number' ? p.height : null,
      pieces: typeof p.pieces === 'number' && p.pieces >= 1 ? p.pieces : 1,
      sides: p.sides === 'single' || p.sides === 'double' ? p.sides : null,
      notes: typeof p.notes === 'string' && p.notes !== '' ? p.notes : null,
      modifiers,
      supplier
    })
  })
  return out
}
